/**
 * @fileoverview User Milestones Tracking Hook
 * 
 * Tracks activation events like first save, first try-on, onboarding completion.
 * Uses session ID to determine if action was in first session.
 */

import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export type MilestoneType = 
  | 'onboarding_complete'
  | 'first_save'
  | 'first_tryon'
  | 'first_poll_vote'
  | 'first_poll_created'
  | 'first_chat';

// Generate or retrieve session ID
const getSessionId = (): string => {
  const key = 'seyu_session_id';
  let sessionId = sessionStorage.getItem(key);
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem(key, sessionId);
  }
  return sessionId;
};

// Check if this is the user's first session (signed up this session)
const getIsFirstSession = (): boolean => {
  return sessionStorage.getItem('seyu_is_first_session') === 'true';
};

export const markFirstSession = () => {
  sessionStorage.setItem('seyu_is_first_session', 'true');
};

export function useMilestones() {
  const { user } = useAuth();
  const trackedRef = useRef<Set<MilestoneType>>(new Set());

  // Reset tracked milestones when user changes
  useEffect(() => {
    trackedRef.current = new Set();
  }, [user?.id]);

  const trackMilestone = useCallback(async (
    type: MilestoneType,
    metadata: Record<string, any> = {}
  ) => {
    if (!user) return;
    
    // Prevent duplicate tracking in same session
    if (trackedRef.current.has(type)) return;
    trackedRef.current.add(type);

    const sessionId = getSessionId();
    const isFirstSession = getIsFirstSession();

    try {
      await (supabase
        .from('user_milestones' as any)
        .upsert({
          user_id: user.id,
          milestone_type: type,
          session_id: sessionId,
          metadata: {
            ...metadata,
            is_first_session: isFirstSession,
            timestamp: new Date().toISOString(),
          },
        }, {
          onConflict: 'user_id,milestone_type',
          ignoreDuplicates: true,
        }) as any);
    } catch (error) {
      // Silently fail - analytics shouldn't break the app
      console.debug('Milestone tracking failed:', error);
    }
  }, [user]);

  return { trackMilestone };
}
